import { storage } from '../storage';

// 定义一个通用的规则接口
export interface Rule {
  isEnabled(): boolean;
  check(): boolean;
  whenEnabled(): boolean | void;
}

// 基础规则类，所有规则都从此类继承
abstract class BaseRule implements Rule {
  protected enabled: boolean;

  constructor(enabled: boolean) {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  abstract check(): boolean;
  abstract whenEnabled(): boolean | void;
}

// 具体规则：当登陆弹窗不出的时候
class SwitchRule extends BaseRule {
  private onOff: boolean;

  constructor(enabled: boolean, onOff: boolean) {
    super(enabled);
    this.onOff = onOff;
  }

  check(): boolean {
    return this.onOff;
  }

  whenEnabled() {}
}

// 具体规则：生效概率
class ProbabilityRule extends BaseRule {
  private id: string;
  private popupHistory: { [npcId: string]: string[] };
  private popupHistoryKey: string;
  private leading: boolean;
  private percent: number;
  // 这个值代表着一个 hook 里，是否只算一次是否生效的结果
  private immutable: boolean;
  private lastValue?: boolean;

  constructor(
    enabled: boolean,
    id: string,
    popupHistoryKey: string,
    percent: number,
    leading: boolean,
    immutable = false
  ) {
    super(enabled);
    this.id = id;
    try {
      this.popupHistory = JSON.parse(storage.getItem(popupHistoryKey) || '{}');
    } catch {
      this.popupHistory = {};
    }
    this.popupHistoryKey = popupHistoryKey;
    this.leading = leading;
    this.percent = percent;
    this.immutable = immutable;
    this.lastValue = undefined;
  }

  private getLastHistory() {
    const lastHistory = this.popupHistory[this.id] || [];
    return lastHistory;
  }

  check(): boolean {
    const history = this.getLastHistory();
    if (this.leading && history.length === 0) return true;
    else if (this.immutable && this.lastValue !== undefined) return this.lastValue;
    else {
      const res = Math.random() < this.percent;
      this.lastValue = res;
      return res;
    }
  }

  whenEnabled(): boolean | void {
    if (!this.popupHistory[this.id]) this.popupHistory[this.id] = [];
    const toDay = new Date().toISOString().split('T')[0];
    this.popupHistory[this.id] = [toDay];
    try {
      storage.setItem(this.popupHistoryKey, JSON.stringify(this.popupHistory));
    } catch {}
  }
}

// 具体规则：最简单的概率规则
class ProbabilityOnceRule extends BaseRule {
  private percent: number;

  constructor(enabled: boolean, percent: number) {
    super(enabled);
    this.percent = percent;
  }

  check(): boolean {
    return Math.random() < this.percent;
  }

  whenEnabled(): boolean | void {}
}

// 具体规则：每个 id 下面最多出 n 次
class TotalTimesLimitRule extends BaseRule {
  private id: string;
  private popupHistory: { [npcId: string]: string[] };
  private popupHistoryKey: string;
  private times: number;

  constructor(enabled: boolean, id: string, popupHistoryKey: string, times = 1) {
    super(enabled);
    this.id = id;
    try {
      this.popupHistory = JSON.parse(storage.getItem(popupHistoryKey) || '{}');
    } catch {
      this.popupHistory = {};
    }
    this.popupHistoryKey = popupHistoryKey;
    this.times = times;
  }

  private getLastHistory() {
    const lastHistory = this.popupHistory[this.id] || [];
    return lastHistory;
  }

  check(): boolean {
    const lastHistory = this.getLastHistory();
    return lastHistory.length < this.times;
  }

  whenEnabled(): boolean | void {
    if (!this.popupHistory[this.id]) this.popupHistory[this.id] = [];
    const toDay = new Date().toISOString().split('T')[0];
    this.popupHistory[this.id].push(toDay);
    try {
      storage.setItem(this.popupHistoryKey, JSON.stringify(this.popupHistory));
    } catch {}
  }
}

// 具体规则：每个 id 下面每天最多出 n 次
class IdDailyLimitRule extends BaseRule {
  private id: string;
  private popupHistory: { [npcId: string]: string[] };
  private popupHistoryKey: string;
  private times: number;

  constructor(enabled: boolean, id: string, popupHistoryKey: string, times = 1) {
    super(enabled);
    this.id = id;
    try {
      this.popupHistory = JSON.parse(storage.getItem(popupHistoryKey) || '{}');
    } catch {
      this.popupHistory = {};
    }
    this.popupHistoryKey = popupHistoryKey;
    this.times = times;
  }

  private getLastHistory() {
    const lastHistory = this.popupHistory[this.id] || [];
    return lastHistory;
  }

  check(): boolean {
    const today = new Date().toISOString().split('T')[0];
    const lastHistory = this.getLastHistory();
    const visitTimesToday = lastHistory.filter((day) => day === today);
    return visitTimesToday.length < this.times;
  }

  whenEnabled(): boolean | void {
    if (!this.popupHistory[this.id]) this.popupHistory[this.id] = [];
    const toDay = new Date().toISOString().split('T')[0];
    this.popupHistory[this.id].push(toDay);
    // 过滤老数据
    this.popupHistory[this.id] = this.popupHistory[this.id].filter((n) => n === toDay);

    try {
      storage.setItem(this.popupHistoryKey, JSON.stringify(this.popupHistory));
    } catch {}
  }
}

// 引导弹窗规则管理类
class GuidePopupRulesManager {
  private rules: Rule[];

  constructor(rules: Rule[]) {
    this.rules = rules;
  }

  shouldShowPopup(): boolean {
    const enable = this.rules.every((rule) => rule.isEnabled() && rule.check());
    if (enable) {
      this.rules.forEach((rule) => rule.whenEnabled());
    }
    return enable;
  }
}

export {
  GuidePopupRulesManager,
  IdDailyLimitRule,
  ProbabilityOnceRule,
  ProbabilityRule,
  SwitchRule,
  TotalTimesLimitRule,
};
